import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Calendar } from "lucide-react";
import { optimizeImage } from "../utils/optimizeImage";

const INTERVAL = 5000; // 5 seconds

// Split editorial layout used by every slide (static, i2030, article) for a consistent look.
// Image sits on the right on desktop, on top on mobile. No CTA overlays are ever drawn on top
// of the image — CTAs live in the text panel so the banner artwork is never obscured.
const HeroSlideEditorial = ({
  overline,
  headingTag: Heading = "h2",
  title,
  italicWord,
  description,
  meta,
  primaryCta,
  secondaryCta,
  image,
  imageSrcSet,
  imageSizes,
  imageWidth,
  imageHeight,
  imageAlt,
  imageFit = "cover", // "cover" for article portraits; "contain" for wide branded banners
  priority = false, // true for the first (LCP) slide: eager + high fetch priority
  testId,
}) => {
  let titleNode = title;
  if (italicWord && title.includes(italicWord)) {
    const [before, ...rest] = title.split(italicWord);
    titleNode = (
      <>
        {before}
        <em className="font-serif italic font-light" style={{ fontStyle: "italic" }}>{italicWord}</em>
        {rest.join(italicWord)}
      </>
    );
  }
  return (
    <div
      className="block relative w-full h-full bg-[#0d1117] group overflow-hidden"
      data-testid={testId}
    >
      <div className="flex flex-col md:flex-row md:items-stretch w-full h-full">
        {/* Text panel */}
        <div className="w-full md:flex-1 md:min-w-0 md:h-full flex md:items-center bg-[#0d1117] order-2 md:order-1 overflow-hidden">
          <div className="px-6 py-6 md:py-8 md:px-10 lg:px-12 xl:px-14 max-w-xl">
            {overline && (
              <span className="editorial-overline-light mb-2 md:mb-3 block" style={{ color: "#B89947" }}>
                {overline}
              </span>
            )}
            <Heading className="font-serif text-2xl md:text-2xl lg:text-3xl xl:text-4xl font-black text-white mb-3 md:mb-4 leading-[1.1] tracking-tight">
              {titleNode}
            </Heading>
            {description && (
              <p className="text-white/65 text-sm md:text-[13px] lg:text-sm mb-4 md:mb-4 line-clamp-3 md:line-clamp-2 lg:line-clamp-3">
                {description}
              </p>
            )}
            {meta}
            {(primaryCta || secondaryCta) && (
              <div className="mt-4 md:mt-5 flex flex-wrap items-center gap-3">
                {primaryCta && (
                  <Link
                    to={primaryCta.href}
                    onClick={(e) => e.stopPropagation()}
                    className="inline-flex items-center gap-2 px-5 md:px-6 py-2.5 md:py-3 font-semibold text-xs md:text-sm tracking-[0.16em] uppercase text-[#0d1117] shadow-lg hover:opacity-90 transition-opacity"
                    style={{ backgroundColor: "#B89947" }}
                    data-testid={primaryCta.testId}
                  >
                    {primaryCta.label} <span aria-hidden>&rarr;</span>
                  </Link>
                )}
                {secondaryCta && (
                  <Link
                    to={secondaryCta.href}
                    onClick={(e) => e.stopPropagation()}
                    className="inline-flex items-center gap-2 px-5 md:px-6 py-2.5 md:py-3 font-semibold text-xs md:text-sm tracking-[0.16em] uppercase text-white/90 hover:text-white transition-colors"
                    style={{ border: "1px solid rgba(255,255,255,0.28)" }}
                    data-testid={secondaryCta.testId}
                  >
                    {secondaryCta.label}
                  </Link>
                )}
              </div>
            )}
          </div>
        </div>
        {/* Image panel — 16:9 aspect on mobile and desktop (matches article banner_image_url uploads). On desktop 62% width, fills full section height (which is calc'd to preserve 16:9 for the image panel). */}
        <div className="relative order-1 md:order-2 w-full md:w-[62%] aspect-[16/9] md:aspect-auto md:h-full md:flex-shrink-0 bg-[#0d1117]">
          {image && <img
            src={image}
            srcSet={imageSrcSet}
            sizes={imageSrcSet ? imageSizes : undefined}
            width={imageWidth}
            height={imageHeight}
            alt={imageAlt || title}
            decoding={priority ? "sync" : "async"}
            loading={priority ? "eager" : "lazy"}
            fetchPriority={priority ? "high" : undefined}
            className={`absolute inset-0 w-full h-full ${imageFit === "contain" ? "object-contain" : "object-cover"}`}
          />}
        </div>
      </div>
    </div>
  );
};

const HeroCarousel = ({ articles }) => {
  const [activeIndex, setActiveIndex] = useState(0);

  // Newest five stories across all sections (API returns newest first).
  const articleSlides = articles.slice(0, 5);

  const slides = [
    { type: "static" },
    { type: "i2030" },
    ...articleSlides.map((a) => ({ type: "article", data: a })),
  ];

  const totalSlides = slides.length;
  // Only the LCP slide loads immediately; the next slide's image is fetched ~2s later so it never
  // competes with the hero image, fonts and data for bandwidth. Rotation keeps the next slide warm.
  const [warm, setWarm] = useState(() => new Set([0]));
  useEffect(() => {
    const t = setTimeout(() => setWarm((w) => new Set([...w, 1 % totalSlides])), 2000);
    return () => clearTimeout(t);
  }, [totalSlides]);
  useEffect(() => {
    if (activeIndex === 0) return;
    setWarm((w) => {
      const n = new Set(w); n.add(activeIndex); n.add((activeIndex + 1) % totalSlides); return n;
    });
  }, [activeIndex, totalSlides]);
  const imgFor = (i, src) => (warm.has(i) ? src : undefined);

  const next = useCallback(() => {
    setActiveIndex((prev) => (prev + 1) % totalSlides);
  }, [totalSlides]);

  const prev = useCallback(() => {
    setActiveIndex((prev) => (prev - 1 + totalSlides) % totalSlides);
  }, [totalSlides]);

  useEffect(() => {
    if (totalSlides <= 1) return;
    const timer = setInterval(next, INTERVAL);
    return () => clearInterval(timer);
  }, [next, totalSlides]);

  const getCategoryLabel = (cat) => {
    const labels = { people: "People", companies: "Companies", brands: "Brands", ideas: "Ideas", initiatives: "Initiatives", specials: "Specials" };
    return labels[cat] || cat;
  };

  const formatDate = (d) => {
    if (!d) return "";
    return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  return (
    <section
      className="relative w-full overflow-hidden bg-[#0d1117] hero-carousel-section"
      data-testid="hero-section"
    >
      <style>{`
        .hero-carousel-section { height: auto; min-height: 0; }
        @media (min-width: 768px) {
          .hero-carousel-section {
            min-height: 0;
            /* Height = 62% viewport width × 9/16 (16:9 aspect). vh cap prevents overflow on ultra-wide monitors. */
            height: min(calc(62vw * 9 / 16), 85vh);
          }
        }
        .hero-slide-layer { position: relative; }
        @media (min-width: 768px) {
          .hero-slide-layer { position: absolute; inset: 0; }
        }
        @media (max-width: 767px) {
          .hero-slide-layer[data-active="false"] { display: none; }
        }
        @keyframes progress {
          from { transform: scaleX(0); }
          to { transform: scaleX(1); }
        }
      `}</style>

      {slides.map((slide, i) => (
        <div
          key={i}
          className="hero-slide-layer transition-opacity duration-700"
          data-active={i === activeIndex ? "true" : "false"}
          style={{ opacity: i === activeIndex ? 1 : 0, pointerEvents: i === activeIndex ? "auto" : "none" }}
        >
          {slide.type === "static" ? (
            <HeroSlideEditorial
              headingTag="h1"
              overline="People Who Matter"
              title="The People Shaping India's Tomorrow"
              italicWord="People"
              description="A new age, technology enabled editorial platform covering the people, brands, companies and initiatives shaping business, culture and contemporary India."
              primaryCta={{ label: "Explore Stories", href: "/category/people", testId: "hero-explore-stories" }}
              secondaryCta={{ label: "Nominate Someone", href: "/contact", testId: "hero-nominate" }}
              image="/static/img/hero-static-2026-960.webp"
              imageSrcSet="/static/img/hero-static-2026-960.webp 960w, /static/img/hero-static-2026-1440.webp 1440w, /static/img/hero-static-2026.webp 1920w"
              imageSizes="(min-width: 768px) 62vw, 100vw"
              imageWidth={1920}
              imageHeight={1081}
              imageAlt="The People Shaping India's Tomorrow"
              imageFit="cover"
              priority
              testId="hero-static-slide"
            />
          ) : slide.type === "i2030" ? (
            <HeroSlideEditorial
              overline="Flagship Event"
              title="India 2030 Leadership Conclave"
              italicWord="India 2030"
              description="The 10th edition: a flagship gathering honouring the leaders, brands and initiatives building tomorrow's India."
              primaryCta={{ label: "Nominations Open", href: "/india-2030-leadership-conclave", testId: "hero-i2030-cta" }}
              image={imgFor(i, "/images/india-2030-banner.webp")}
              imageAlt="India 2030 Leadership Conclave, 10th Edition"
              imageFit="contain"
              testId="hero-i2030-slide"
            />
          ) : (
            <HeroSlideEditorial
              overline={getCategoryLabel(slide.data.category)}
              title={slide.data.title}
              description={slide.data.excerpt}
              meta={(
                <div className="flex items-center gap-3 text-white/45 text-xs md:text-sm">
                  <span className="text-white/70 font-medium">{slide.data.author}</span>
                  <span className="w-1 h-1 rounded-full bg-white/25"></span>
                  <span className="flex items-center gap-1.5">
                    <Calendar className="w-3.5 h-3.5" />
                    {formatDate(slide.data.published_at)}
                  </span>
                </div>
              )}
              primaryCta={{ label: "Read Story", href: `/article/${slide.data.slug}`, testId: `hero-article-cta-${slide.data.slug}` }}
              image={imgFor(i, optimizeImage(slide.data.banner_image_url || slide.data.image_url, { width: 1600 }))}
              imageAlt={slide.data.title}
              testId={`hero-article-${slide.data.slug}`}
            />
          )}
        </div>
      ))}

      {/* Navigation arrows */}
      <button
        onClick={prev}
        className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center text-white/50 hover:text-white hover:bg-white/10 transition-all z-10"
        aria-label="Previous slide"
        data-testid="hero-prev"
      >
        <ChevronLeft className="w-5 h-5" />
      </button>
      <button
        onClick={next}
        className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center text-white/50 hover:text-white hover:bg-white/10 transition-all z-10"
        aria-label="Next slide"
        data-testid="hero-next"
      >
        <ChevronRight className="w-5 h-5" />
      </button>

      {/* Dot indicators */}
      <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-2 z-10">
        {slides.map((_, i) => (
          <button
            key={i}
            onClick={() => setActiveIndex(i)}
            className={`h-0.5 transition-all duration-300 ${
              i === activeIndex ? "w-8 bg-white" : "w-4 bg-white/30 hover:bg-white/50"
            }`}
            aria-label={`Go to slide ${i + 1}`}
            data-testid={`hero-dot-${i}`}
          />
        ))}
      </div>

      {/* Progress bar */}
      <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-white/10 z-10">
        <div
          className="h-full bg-white/40"
          style={{
            animation: `progress ${INTERVAL}ms linear infinite`,
            width: "100%",
            transformOrigin: "left",
          }}
        />
      </div>
    </section>
  );
};

export default HeroCarousel;
